import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Link, useHistory } from 'react-router-dom'
import { IconButton, makeStyles, Snackbar, Tooltip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import EditIcon from '@material-ui/icons/Edit'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import LocalOfferIcon from '@material-ui/icons/LocalOffer'
import CodeIcon from '@material-ui/icons/Code'
import AdminPage from '../../../admin/shared/AdminPage'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import Loading from '../../../../molecules/Loading'
import { smartEndpoints } from '../../../../../api/smartEndpoints'
import PolicyManagement from './PolicyManagement'
import TagManagementModal from './TagManagementModal'
import InvocationCodesModal from './InvocationCodesModal'
import { formatRelativeTime } from './formatRelativeTime'

const useStyles = makeStyles(() => ({
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 2px',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    borderRadius: 'var(--border-radius-sm) var(--border-radius-sm) 0 0',
    marginBottom: 'var(--margin-md)'
  },
  tab: {
    padding: 'var(--padding-sm) var(--padding-md)',
    fontSize: 'var(--text-size-sm)',
    fontWeight: 500,
    cursor: 'pointer',
    color: 'var(--color-neutral-400)',
    borderBottom: '2px solid transparent',
    transition: 'color var(--transition-duration-short, 0.15s) ease',
    userSelect: 'none',
    '&:hover': {
      color: 'var(--color-neutral-700)'
    }
  },
  tabActive: {
    color: 'var(--color-primary-700)',
    borderBottomColor: 'var(--color-primary-500)',
    fontWeight: 600
  },
  tableWrap: {
    '& .operational-table > thead > tr > th': {
      fontSize: 'var(--text-size-xs)',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'var(--color-neutral-500)',
      padding: '10px 18px',
      borderBottom: '1px solid var(--color-neutral-200)'
    },
    '& .operational-table > thead': {
      backgroundColor: 'var(--color-neutral-050)'
    },
    '& .operational-table > tbody > tr > td': {
      padding: '10px 18px 10px 18px'
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px var(--padding-md)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  endpointCount: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  searchInput: {
    padding: '5px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    outline: 'none',
    width: '200px',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  mono: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 'var(--text-size-xs)'
  },
  versionBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-600)'
  },
  modeBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    lineHeight: '16px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },
  modeRead: {
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    color: 'var(--color-primary-700, #1d4ed8)'
  },
  modeWrite: {
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    color: 'var(--color-danger-700, #b91c1c)'
  },
  actionBtn: {
    color: 'var(--color-neutral-300)',
    transition: 'color var(--transition-duration-short, 0.15s) ease',
    '&:hover': {
      color: 'var(--color-primary-600)',
      backgroundColor: 'var(--color-primary-050, #eff6ff)'
    }
  },
  timestampText: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)'
  },
  liveBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  notLiveBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 500,
    fontStyle: 'italic',
    backgroundColor: 'var(--color-neutral-050)',
    color: 'var(--color-neutral-400)'
  }
}))

const TABS = [
  { id: 'endpoints', label: 'Smart Endpoints' },
  { id: 'policies', label: 'Policies' }
]

function SmartEndpointsHome ({ root }) {
  const classes = useStyles()
  const history = useHistory()
  const [activeTab, setActiveTab] = useState('endpoints')
  const [searchText, setSearchText] = useState('')
  const [toast, setToast] = useState(null)
  const [tagModalCode, setTagModalCode] = useState(null)
  const [codesModalCode, setCodesModalCode] = useState(null)

  const { data: programs, isLoading } = smartEndpoints.useListPrograms()

  const handleDuplicate = useCallback((program) => {
    const baseName = program.displayName ? `${program.displayName} (copy)` : ''
    const params = new URLSearchParams({
      duplicateFrom: program.code,
      name: baseName
    })
    history.push(`${root}/analysis/smart-endpoints/new?${params.toString()}`)
    setToast({ message: `Duplicating "${program.code}" — fill in a new code and save`, severity: 'info' })
  }, [history, root])

  const tableData = useMemo(() => {
    if (!programs) return []
    return programs.map(p => {
      const config = p.config || {}
      return {
        ...p,
        mode: config.mode === 'command' ? 'command' : 'query',
        lastModified: p.modifiedDate || p.createdDate || null,
        lastModifiedBy: p.modifiedBy || p.createdBy || null
      }
    })
  }, [programs])

  /* eslint-disable react/prop-types */
  const columns = useMemo(() => [
    {
      id: 'code',
      accessor: 'code',
      Header: 'ID',
      Cell: ({ value }) => <span className={classes.mono}>{value}</span>
    },
    { id: 'displayName', accessor: 'displayName', Header: 'Name' },
    {
      id: 'liveVersion',
      accessor: 'liveVersion',
      Header: 'Live',
      Cell: ({ row }) => {
        const live = row.original.liveVersion
        if (live == null) {
          return <span className={classes.notLiveBadge}>not live</span>
        }
        return <span className={classes.liveBadge}>v{live}</span>
      }
    },
    {
      id: 'version',
      accessor: 'version',
      Header: 'Latest',
      Cell: ({ value }) => <span className={classes.versionBadge}>v{value}</span>
    },
    {
      id: 'mode',
      accessor: 'mode',
      Header: 'Mode',
      Cell: ({ value }) => (
        <span className={`${classes.modeBadge} ${value === 'command' ? classes.modeWrite : classes.modeRead}`}>
          {value === 'command' ? 'read/write' : 'read only'}
        </span>
      )
    },
    {
      id: 'lastModified',
      accessor: 'lastModified',
      Header: 'Last Modified',
      Cell: ({ row }) => {
        const when = row.original.lastModified
        const who = row.original.lastModifiedBy
        if (!when) return <span className={classes.timestampText}>—</span>
        return (
          <Tooltip title={`${new Date(when).toLocaleString()}${who ? ` by ${who}` : ''}`} arrow>
            <span className={classes.timestampText}>
              {formatRelativeTime(when)}{who ? ` · ${who}` : ''}
            </span>
          </Tooltip>
        )
      }
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '2px' }}>
          <Tooltip title='Invocation codes' arrow>
            <IconButton
              size='small'
              className={classes.actionBtn}
              onClick={() => setCodesModalCode(row.original.code)}
            >
              <CodeIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Manage tags' arrow>
            <IconButton
              size='small'
              className={classes.actionBtn}
              onClick={() => setTagModalCode(row.original.code)}
            >
              <LocalOfferIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Duplicate endpoint' arrow>
            <IconButton
              size='small'
              className={classes.actionBtn}
              onClick={() => handleDuplicate(row.original)}
            >
              <FileCopyIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Edit endpoint' arrow>
            <IconButton
              size='small'
              className={classes.actionBtn}
              onClick={() => history.push(`${root}/analysis/smart-endpoints/${row.original.code}`)}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>
      )
    }
  ], [classes, history, root, handleDuplicate])
  /* eslint-enable react/prop-types */

  const defaultSort = useMemo(() => [{ id: 'code', desc: false }], [])

  return (
    <AdminPage>
      <div className={classes.tabBar} role='tablist'>
        {TABS.map(tab => (
          <div
            key={tab.id}
            role='tab'
            aria-selected={activeTab === tab.id}
            className={`${classes.tab} ${activeTab === tab.id ? classes.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
        <div style={{ marginLeft: 'auto', padding: '6px 0' }}>
          {activeTab === 'endpoints' && (
            <Link to={`${root}/analysis/smart-endpoints/new`}>
              <SydButton size='sm' variant='primary' icon='add'>New</SydButton>
            </Link>
          )}
        </div>
      </div>

      {activeTab === 'endpoints' && (
        isLoading
          ? <Loading />
          : tableData.length === 0
            ? (
              <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 'var(--text-size-sm)', color: 'var(--color-neutral-500)', marginBottom: '24px', lineHeight: 1.7 }}>
                  No smart endpoints yet. Create one to get started.
                </div>
                <Link to={`${root}/analysis/smart-endpoints/new`}>
                  <SydButton size='sm' variant='primary' icon='add'>New Smart Endpoint</SydButton>
                </Link>
              </div>
            )
            : (
              <>
                <div className={classes.toolbar}>
                  <span className={classes.endpointCount}>
                    {tableData.length} {tableData.length === 1 ? 'endpoint' : 'endpoints'}
                  </span>
                  <input
                    className={classes.searchInput}
                    placeholder='Search endpoints…'
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className={classes.tableWrap}>
                  <OperationalTable
                    variant='blank'
                    itemName='Smart Endpoints'
                    columns={columns}
                    data={tableData}
                    defaultSort={defaultSort}
                    searchText={searchText}
                  />
                </div>
              </>
            )
      )}

      {activeTab === 'policies' && (
        <PolicyManagement />
      )}

      {tagModalCode && (
        <TagManagementModal
          code={tagModalCode}
          onClose={() => setTagModalCode(null)}
        />
      )}

      {codesModalCode && (
        <InvocationCodesModal
          code={codesModalCode}
          onClose={() => setCodesModalCode(null)}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </AdminPage>
  )
}

SmartEndpointsHome.propTypes = {
  root: PropTypes.string
}

export default SmartEndpointsHome
